/**
 *
 * @module tokenManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/token.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Access from './access';
import AccessState from './accessState';
import AccessStateData from './accessStateData';
import AccessStorage from './accessStorage';
import AccessToken from './accessToken';
import AccessTokenProvider from './accessTokenProvider';
import AccessContextState from './accessContextState';
import TokenRefreshFailure from './tokenRefreshFailure';
import Device from './../device/entry';
import GeoProvider from './../platforms/geoProvider';

const DeviceManager = Device.DeviceManager;
const Util = Services.Util;
const DeviceGrant = Services.Token.DeviceGrant;
const RefreshGrant = Services.Token.RefreshGrant;
const ExchangeGrant = Services.Token.ExchangeGrant;
const Grant = Services.Token.Grant;
const TokenClient = Services.Token.TokenClient;
const TokenRequestBuilder = Services.Token.TokenRequestBuilder;
const TokenManagerConfiguration = Services.Configuration.TokenManagerConfiguration;

const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;
const ExceptionReference = Services.Exception.ExceptionReference;
const ErrorReason = Services.Exception.ErrorReason;

const {
    boolean,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class TokenManager
 * @extends {IAccessTokenProvider}
 * @implements {IAccessTokenUpdater}
 * @see https://nodejs.org/api/events.html
 * @desc Manages the current AccessContext, allows tokens to be exchanged to get a new AccessContext
 * and auto refreshes the AccessContext before it expires.
 *
 */
export default class TokenManager extends AccessTokenProvider {

    /**
     *
     * @param {String} apiKey
     * @param {SDK.Services.Configuration.TokenManagerConfiguration} tokenManagerConfiguration
     * @param {SDK.Services.Token.TokenClient} tokenClient
     * @param {GeoProvider} [geoProvider=null]
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {AccessStorage} accessStorage
     * @param {SDK.Device.DeviceManager} deviceManager
     * @param {String} versionShort
     *
     */
    constructor(apiKey, tokenManagerConfiguration, tokenClient, geoProvider = null,
        logger = Logging, accessStorage, deviceManager, versionShort) {

        const errorMessage = {
            ctor: `TokenManager(apiKey, tokenManagerConfiguration, tokenClient, geoProvider, logger, accessStorage, deviceManager, versionShort)`, // eslint-disable-line
            get apiKey() {
                return `${this.ctor} apiKey should be a String with length`;
            },
            get tokenManagerConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.TokenManagerConfiguration instance`;
            },
            get tokenClient() {
                return `${this.ctor} needs SDK.Services.Token.TokenClient instance`;
            },
            get geoProvider() {
                return `${this.ctor} needs GeoProvider instance`;
            },
            get accessStorage() {
                return `${this.ctor} needs AccessStorage instance`;
            },
            get deviceManager() {
                return `${this.ctor} needs SDK.Device.DeviceManager instance`;
            },
            get versionShort() {
                return `${this.ctor} versionShort should be a String with length`;
            }
        };

        nonEmptyString(apiKey, errorMessage.apiKey, InvalidArgumentException);
        instanceStrict(tokenManagerConfiguration, TokenManagerConfiguration, errorMessage.tokenManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(tokenClient, TokenClient, errorMessage.tokenClient, InvalidArgumentException);

        if (Check.assigned(geoProvider)) {
            instanceStrict(geoProvider, GeoProvider, errorMessage.geoProvider, InvalidArgumentException);
        }

        instanceStrict(accessStorage, AccessStorage, errorMessage.accessStorage, InvalidArgumentException);
        instanceStrict(deviceManager, DeviceManager, errorMessage.deviceManager, InvalidArgumentException);
        nonEmptyString(versionShort, errorMessage.versionShort, InvalidArgumentException);

        super();

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.apiKey = apiKey;

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.TokenManagerConfiguration}
         *
         */
        this.config = tokenManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Services.Token.TokenClient}
         *
         */
        this.client = tokenClient;

        /**
         *
         * @access private
         * @type {GeoProvider}
         *
         */
        this.geoProvider = geoProvider || new GeoProvider(logger);

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {AccessStorage}
         *
         */
        this.storage = accessStorage;

        /**
         *
         * @access private
         * @type {SDK.Device.DeviceManager}
         *
         */
        this.deviceManager = deviceManager;

        /**
         *
         * @access private
         * @type {Number} timeout ID or -1
         *
         */
        this.refreshTimeoutId = -1;

        /**
         *
         * @access private
         * @type {Number}
         * @desc retry counter for access token refresh
         *
         */
        this.retryAttempt = 0;

        /**
         *
         * @access private
         * @type {Number} timeout ID or -1
         *
         */
        this.retryDelay = -1;

        /**
         *
         * @access private
         * @type {Number}
         * @desc retry counter for device grant reset
         *
         */
        this.deviceGrantResetRetry = 0;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.deviceGrantResetMaxAttempts = 5;

        /**
         *
         * @access private
         * @type {String}
         * @since 3.2.0
         * @desc The shortened version String, without the build SHA.
         * @example '3.9'
         *
         */
        this.versionShort = versionShort;

        this.logger.log(TokenManager.name, 'Created');

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.Grant} grant
     * @param {AccessContextState} accessContextState
     * @desc Exchanges a grant for an access token.
     * @returns {Promise<Void>}
     *
     */
    exchangeGrant(grant, accessContextState) {

        const errorMessage = {
            method: 'TokenManager.exchangeGrant(grant, accessContextState)',
            get grant() {
                return `${this.method} needs Grant instance`;
            },
            get accessContextState() {
                return `${this.method} needs AccessContextState instance`;
            }
        };

        instanceStrict(grant, Grant, errorMessage.grant, InvalidArgumentException);
        instanceStrict(accessContextState, AccessContextState, errorMessage.accessContextState, InvalidArgumentException); // eslint-disable-line

        const { config } = this;
        const subjectTokenType = Util.getSafe(() => config.extras.subjectTokenTypes[grant.subjectTokenTypeKey]);
        const exchangeGrant = new ExchangeGrant(grant, subjectTokenType);

        return this.exchangeRequest(exchangeGrant, accessContextState);

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.TokenRequestBuilder} tokenRequestBuilder
     * @param {AccessContextState} accessContextState
     * @returns {Promise<Void>}
     *
     */
    exchangeRequest(tokenRequestBuilder, accessContextState) {

        const errorMessage = {
            method: 'TokenManager.exchangeRequest(tokenRequestBuilder, accessContextState)',
            get tokenRequestBuilder() {
                return `${this.method} needs SDK.Services.Token.TokenRequestBuilder instance`;
            },
            get accessContextState() {
                return `${this.method} needs AccessContextState instance`;
            }
        };

        instanceStrict(tokenRequestBuilder, TokenRequestBuilder, errorMessage.tokenRequestBuilder, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessContextState, AccessContextState, errorMessage.accessContextState, InvalidArgumentException); // eslint-disable-line

        const { apiKey, client, deviceManager } = this;

        return this.geoProvider.getGeoLocation().then((geoLocation) => {

            const { latitude, longitude } = geoLocation;

            return tokenRequestBuilder.build(latitude, longitude);

        }).then((tokenRequest) => {

            return client.exchange(tokenRequest, apiKey);

        }).then((accessContext) => {

            return this.processAccess(accessContext, accessContextState);

        }).catch((exception) => {

            const { deviceGrantResetRetry, deviceGrantResetMaxAttempts } = this;

            const errorCode = Util.getSafe(() => exception.reasons[0].code);
            const isDeviceGrant = Check.instanceStrict(tokenRequestBuilder.grant, DeviceGrant);
            const isInvalid = Check.equal(errorCode, 'invalid_grant');

            // process invalid grants or simply fail through with the exception
            if (isInvalid) {

                // current grant is invalid, clear out the stored device grant
                return deviceManager.deviceGrantStorage.clear().then(() => {

                    // process the provided device grant, a new device grant (anon session)
                    // will be established to replace the current invalid device grant
                    if (isDeviceGrant) {

                        // the number of attempts is capped to prevent a recursive failure loop
                        if (deviceGrantResetRetry > deviceGrantResetMaxAttempts) {
                            return Promise.reject(exception);
                        }

                        this.deviceGrantResetRetry++;

                        // establish a new device grant, clear out the reset retry counter on success
                        return deviceManager.createDeviceGrant().then((deviceGrant) => {

                            return this.reset(deviceGrant);

                        }).then(() => {

                            this.deviceGrantResetRetry = 0;

                            this.emit('TokenRefreshFailed', new TokenRefreshFailure(exception));

                            return Promise.resolve();

                        });

                    }

                    return Promise.reject(exception);

                });

            }

            return Promise.reject(exception);

        });

    }

    /**
     *
     * @access private
     * @desc Gets the access state of the user in a format that can be
     * used to rebuild the state in an external system.
     * @returns {AccessState}
     *
     */
    getAccessState() {

        const { storage, versionShort } = this;
        const access = storage.getAccess();

        return new AccessState(
            versionShort,
            new AccessStateData(
                Util.getSafe(() => access.context.token),
                Util.getSafe(() => access.context.refreshToken),
                Util.getSafe(() => access.contextState)
            )
        );

    }

    /**
     *
     * @access private
     * @param {Boolean} [force=false] - Forces a refresh regardless of the token's current expiration
     * @desc Refreshes the access token if it is about to expire
     * @note Ensure a proper Access is available before executing logic
     * @note The refreshToken is recast, however the contextState is not because it
     * is always expected to be a instance of AccessContextState at this point
     * @returns {Promise<Void>}
     *
     */
    refreshAccessToken(force = false) {

        const errorMessage = {
            method: 'TokenManager.refreshAccessToken(force)',
            get force() {
                return `${this.method} force should be a Boolean`;
            }
        };

        boolean(force, errorMessage.force, InvalidArgumentException);

        const { config, storage, deviceManager } = this;
        const access = storage.getAccess();

        if (Check.instanceStrict(access, Access)) {

            let refresh = force;

            if (!force) {

                const timeToRefresh = config.extras.refreshThreshold * (access.context.expiresIn * 1000);
                const refreshAt = (access.context.issuedAt) + timeToRefresh;

                if (Date.now() >= refreshAt) {
                    refresh = true;
                }

            }

            if (refresh) {

                const refreshGrant = new RefreshGrant(access.context.refreshToken);

                // exchange the grant, process failures if necessary
                return this.exchangeRequest(refreshGrant, access.contextState).catch((exception) => {

                    const deviceGrantStorage = deviceManager.deviceGrantStorage;
                    const storedDeviceGrant = deviceGrantStorage.getDeviceGrant();

                    // device grant is not stored, establish a new device grant (anon session)
                    // note that a device grant must always be established
                    if (Check.not.assigned(storedDeviceGrant)) {

                        const { deviceGrantResetRetry, deviceGrantResetMaxAttempts } = this;

                        // the number of attempts is capped to prevent a recursive failure loop
                        if (deviceGrantResetRetry > deviceGrantResetMaxAttempts) {
                            return Promise.reject(exception);
                        }

                        this.deviceGrantResetRetry++;

                        // establish a new device grant, clear out the reset retry counter on success
                        return deviceManager.createDeviceGrant().then((deviceGrant) => {

                            return this.reset(deviceGrant);

                        }).then(() => {

                            this.deviceGrantResetRetry = 0;

                            this.emit('TokenRefreshFailed', new TokenRefreshFailure(exception));

                            return Promise.resolve();

                        });

                    }

                    return Promise.reject(exception);

                });

            }

            return Promise.resolve();

        }

        const reasons = [new ErrorReason('', 'A successful token exchange is required before refresh is allowed')];
        const exceptionData = ExceptionReference.common.invalidState;

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Grant} grant - An object that is capable for providing all the data needed to perform a token exchange.
     * @desc Exchanges a grant for an access token and starts the auto refresh timer.
     * @returns {Promise<Void>}
     *
     */
    reset(grant) {

        const accessContextState = new AccessContextState();

        this.clearRetryRefresh();

        return this.exchangeGrant(grant, accessContextState);

    }

    /**
     *
     * @access private
     * @param {String} accessState - The serialized state retrieved via SdkSession#getAccessState
     * could be retrieved from another device, instance, etc...
     * @desc Exchanges a grant for an access token and starts the auto refresh timer.
     * @returns {Promise<Void>}
     *
     */
    restoreAccessState(accessState) {

        const errorMessage = {
            method: 'TokenManager.restoreAccessState(accessState)',
            get accessState() {
                return `${this.method} accessState should be a String with length`;
            }
        };

        nonEmptyString(accessState, errorMessage.accessState, InvalidArgumentException);

        const { data } = JSON.parse(accessState);
        const { refreshToken, contextState } = data;

        const refreshGrant = new RefreshGrant(refreshToken);
        const accessContextState = new AccessContextState(contextState.modes);

        this.clearRetryRefresh();

        return this.exchangeRequest(refreshGrant, accessContextState);

    }

    /**
     *
     * @access private
     * @param {String} mode - Unique key for a given state.
     * @returns {Promise<Boolean>} A boolean indicating whether or not the access context is set for the given state.
     *
     */
    hasAccessMode(mode) {

        const { storage } = this;

        const access = storage.getAccess();

        if (Check.not.assigned(access)) {
            return Promise.resolve(false);
        }

        const accessState = Util.getSafe(() => access.contextState.hasMode(mode), false);

        return Promise.resolve(accessState);

    }

    /**
     *
     * @access private
     * @param {AccessContext} accessContext
     * @param {AccessContextState} accessContextState
     * @emits {AccessChange} The event raised each time a access token is updated.
     * @desc utility function, handles common functionality for exchange
     * @returns {Promise<Void>}
     *
     */
    processAccess(accessContext, accessContextState) {

        const { logger } = this;
        const access = new Access(accessContext, accessContextState);

        return this.storage.saveAccess(access).then(() => {

            logger.info(TokenManager.name, 'Dispatch AccessChange event.');

            this.emit('AccessChange', new AccessToken(accessContext.token));
            this.enqueueRefresh();

            return Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @desc uses setTimeout to enqueue a call to #refreshAccessToken
     *
     */
    enqueueRefresh() {

        const { logger, config, storage } = this;

        const access = storage.getAccess();
        const now = Date.now();

        if (access) {

            this.clearRetryRefresh();

            const hasAccessContext = Check.assigned(access.context);
            const hasRefreshToken = hasAccessContext && Check.assigned(access.context.refreshToken);

            const timeToRefresh = config.extras.refreshThreshold * (access.context.expiresIn * 1000);
            const refreshAt = (access.context.issuedAt) + timeToRefresh;
            const delay = refreshAt - now;

            if (!hasRefreshToken) {
                logger.warn(TokenManager.name, 'refreshToken missing, skipping refresh logic.');
                return;
            }

            this.retryAttempt = 0;
            this.retryDelay = config.extras.autoRefreshRetryPolicy.retryBasePeriod;

            this.refreshTimeoutId = setTimeout(this.retryRefresh.bind(this), delay);

            return;

        }

        const reasons = [new ErrorReason('', 'A successful token exchange is required before refresh is allowed')];
        const exceptionData = ExceptionReference.common.invalidState;

        throw new ServiceException({ reasons, exceptionData });

    }

    /**
     *
     * @access private
     * @desc attempts to retry refreshing access
     * @emits {TokenRefreshFailed} The event raised when automatic token refresh fails.
     * @returns {Promise<Void>}
     *
     */
    retryRefresh() {

        const { config, logger } = this;

        const autoRefreshRetryPolicy = config.extras.autoRefreshRetryPolicy;
        const retryMaxAttempts = autoRefreshRetryPolicy.retryMaxAttempts;
        const retryMultiplier = autoRefreshRetryPolicy.retryMultiplier;

        this.clearRetryRefresh();

        return this.refreshAccessToken(true).catch((ex) => {

            if (this.retryAttempt < retryMaxAttempts) {

                this.refreshTimeoutId = setTimeout(this.retryRefresh.bind(this), this.retryDelay);
                this.retryDelay = this.retryDelay * retryMultiplier;
                this.retryAttempt++;

            } else {

                logger.error(TokenManager.name, 'Token refresh failed.');
                logger.info(TokenManager.name, 'Dispatch TokenRefreshFailed event.');

                this.emit('TokenRefreshFailed', new TokenRefreshFailure(ex));

            }

        });

    }

    /**
     *
     * @access private
     * @desc cancels all pending actions
     *
     */
    clearRetryRefresh() {

        clearTimeout(this.refreshTimeoutId);

        this.refreshTimeoutId = -1;

    }

}



// WEBPACK FOOTER //
// ./src/token/tokenManager.js